import {
	createFileRoute,
	useParams,
	useRouteContext,
} from "@tanstack/react-router";
import { graphql, useLazyLoadQuery, fetchQuery } from "react-relay";
import { Event } from "@/ui/event";
import type { eventosBySlugQuery } from "./__generated__/eventosBySlugQuery.graphql";
import type { eventosHeadQuery } from "./__generated__/eventosHeadQuery.graphql";
import { getImageUrl } from "@/utils/getImageUrl";
import { environment } from "@/relay/environment";

export const Route = createFileRoute("/_visibility/_topnav/eventos/$slug/")({
	component: RouteComponent,
	loader: ({ context }) => {
		return { club: context.club };
	},
	head: async ({ params: { slug }, loaderData }) => {
		if (!loaderData?.club) {
			return {};
		}

		const activeDomain = loaderData.club.domains.find((d) => d.enabled)?.domain;
		const clubUrl = activeDomain
			? `https://${activeDomain}`
			: "https://scarf.club";

		const data = await fetchQuery<eventosHeadQuery>(
			environment,
			graphql`
	    query eventosHeadQuery($club: String!, $slug: String!) {
	        club(slug: $club) {
						name
	          event(slug: $slug) {
	            id
							title
							description
							coverPhoto {
								signedUrl
							}
							startDate
	          }
	        }
	    }
	  `,
			{ club: loaderData.club.slug!, slug },
		).toPromise();

		if (!data?.club.event) {
			return {};
		}

		const ogImageUrl = data.club.event.coverPhoto?.signedUrl
			? getImageUrl(data.club.event.coverPhoto.signedUrl, {
					format: "png",
					width: 1200,
					height: 630,
					quality: 80,
					maxBytes: 600,
				})
			: undefined;

		const canonicalUrl = `${clubUrl}/eventos/${slug}`;

		const structuredData = {
			"@context": "https://schema.org",
			"@type": "Event",
			name: data.club.event.title,
			description: data.club.event.description,
			startDate: data.club.event.startDate,
			url: canonicalUrl,
			organizer: {
				"@type": "Organization",
				name: data.club.name,
				url: clubUrl,
			},
		};

		return {
			meta: [
				{
					title: `${data.club.event.title} | ${data.club.name}`,
				},
				{
					name: "canonical",
					content: canonicalUrl,
				},
				{
					property: "og:type",
					content: "event",
				},
				{
					property: "og:title",
					content: `${data.club.event.title} | ${data.club.name}`,
				},
				{
					property: "og:url",
					content: canonicalUrl,
				},
				...(ogImageUrl
					? [
							{
								property: "og:image",
								content: ogImageUrl,
							},
							{
								property: "og:image:secure_url",
								content: ogImageUrl,
							},
							{
								property: "og:image:width",
								content: "1200",
							},
							{
								property: "og:image:height",
								content: "630",
							},
							{
								property: "twitter:image",
								content: ogImageUrl,
							},
						]
					: []),
			],
			links: [
				{
					rel: "canonical",
					href: canonicalUrl,
				},
			],
			scripts: [
				{
					type: "application/ld+json",
					innerHTML: JSON.stringify(structuredData),
				},
			],
		};
	},
});

function RouteComponent() {
	const { club } = useRouteContext({ from: "__root__" });
	const { slug } = useParams({ from: Route.id });
	const data = useLazyLoadQuery<eventosBySlugQuery>(
		graphql`
    query eventosBySlugQuery($club: String!, $slug: String!) {
      club(slug: $club) {
        event(slug: $slug) {
          id
          ...eventFragment
        }
      }
    }
  `,
		{ club: club.slug, slug },
	);

	return (
		<div className="w-full flex items-start justify-center p-6">
			<Event event={data.club.event} />
		</div>
	);
}
